// src/pages/groups/QuizImportModal.jsx
// استيراد أسئلة الاختبار من التطبيقات (Academy / Languages / General / ESP / STEP / IELTS)
import { useEffect, useMemo, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { quizService } from "../../services/quizService";
import { getErrorMessage } from "../../services/groupService";
import { Modal, Spinner } from "./groupsShared";
import RichText from "./RichText";

const LEVEL_LABELS = { language: "اللغة", category: "التصنيف", exam: "الاختبار", skill: "المهارة" };
const KIND_LABELS = { VOCABULARY: "مفردات", GRAMMAR: "قواعد", READING: "قراءة", LISTENING: "استماع", SPEAKING: "تحدث" };
const SKILL_LABELS = { ...KIND_LABELS, MIXED: "مختلطة", GENERAL_PATH: "مختلطة", WRITING: "كتابة" };
const DIFFICULTY_LABELS = { EASY: "سهل", MEDIUM: "متوسط", HARD: "صعب" };
const LETTERS = ["A", "B", "C", "D"];
const LETTER_AR = { A: "أ", B: "ب", C: "ج", D: "د" };
const MODES = [
  { key: "pick", label: "اختيار أسئلة محددة" },
  { key: "all", label: "جميع الأسئلة" },
  { key: "random", label: "عدد عشوائي" },
];

const refKey = (kind, id) => `${kind}:${id}`;
const paramsFrom = (path) => Object.fromEntries(path.map((p) => [`${p.level}_id`, p.id]));
const hasText = (html) => !!html && html.replace(/<[^>]*>/g, "").trim() !== "";

// عدد الأسئلة التي سيتم استيرادها من مهارة واحدة حسب طريقة الاختيار
const selectedCount = (s) => {
  const available = Number(s.questions_count) || 0;
  if (s.mode === "pick") return s.refs.length;
  if (s.mode === "all") return available;
  return Math.min(Number(s.random_count) || 0, available);
};

/* ───────────── تفاصيل السؤال الكاملة ───────────── */

export function QuestionDetails({ q }) {
  return (
    <div style={{ padding: "6px 14px 14px", background: "#fafafa", fontSize: 13 }}>
      {q.question_image && (
        <img
          src={q.question_image}
          alt=""
          style={{ maxWidth: "100%", maxHeight: 220, borderRadius: 6, marginBottom: 10, display: "block" }}
        />
      )}

      <div style={{ display: "grid", gap: 6 }}>
        {LETTERS.map((l) => {
          const correct = q.correct_answer === l;
          return (
            <div
              key={l}
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
                padding: "6px 10px",
                borderRadius: 6,
                border: `1px solid ${correct ? "#86efac" : "#e5e7eb"}`,
                background: correct ? "#f0fdf4" : "#fff",
              }}
            >
              <b style={{ minWidth: 18 }}>{LETTER_AR[l]}</b>
              <div style={{ flex: 1, minWidth: 0 }}>
                <RichText html={q.choices?.[l]} />
              </div>
              {correct && <span className="gp-badge ok">الصحيحة</span>}
            </div>
          );
        })}
      </div>

      {hasText(q.explanation) && (
        <div style={{ marginTop: 10 }}>
          <b style={{ display: "block", marginBottom: 4 }}>الشرح</b>
          <RichText html={q.explanation} />
        </div>
      )}
      {hasText(q.english_explanation) && (
        <div style={{ marginTop: 10 }}>
          <b style={{ display: "block", marginBottom: 4 }}>الشرح بالإنجليزية</b>
          <RichText html={q.english_explanation} />
        </div>
      )}

      <div className="gp-actions" style={{ marginTop: 10 }}>
        <span className="gp-badge">{q.points} نقطة</span>
        <span className="gp-badge">{DIFFICULTY_LABELS[q.difficulty]}</span>
        {q.context_title && <span className="gp-badge">{q.context_title}</span>}
      </div>
    </div>
  );
}

/* ───────────── صف سؤال واحد ───────────── */

function QuestionRow({ q, checked, onToggle }) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ borderBottom: "1px solid #f1f1f1", opacity: q.already_added ? 0.6 : 1 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "8px 10px" }}>
        <label
          style={{
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
            flex: 1,
            minWidth: 0,
            cursor: q.already_added ? "not-allowed" : "pointer",
          }}
        >
          <input type="checkbox" checked={checked} disabled={q.already_added} onChange={onToggle} />
          <span style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
            <RichText html={q.question_text} clamp={open ? undefined : 2} />
            {q.context_title && (
              <small className="gp-muted" style={{ display: "block", marginTop: 2 }}>
                {q.context_title}
              </small>
            )}
          </span>
        </label>

        <span className="gp-badge">{KIND_LABELS[q.kind]}</span>
        <span className="gp-badge">{DIFFICULTY_LABELS[q.difficulty]}</span>
        {q.already_added && <span className="gp-badge ok">مُضاف</span>}

        <button
          type="button"
          className="gp-icon-btn"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "إخفاء تفاصيل السؤال" : "عرض تفاصيل السؤال"}
          aria-expanded={open}
          title={open ? "إخفاء التفاصيل" : "عرض التفاصيل"}
        >
          {open ? <EyeOff size={15} /> : <Eye size={15} />}
        </button>
      </div>

      {open && <QuestionDetails q={q} />}
    </div>
  );
}

/* ───────────── اختيار أسئلة محددة من مهارة ───────────── */

function SkillQuestionPicker({ app, skill, quizId, refs, onChange }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [difficulty, setDifficulty] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchPage = async (offset) => {
    setLoading(true);
    setError("");
    try {
      const res = await quizService.sourceQuestions(app, skill.id, {
        quiz_id: quizId,
        limit: 100,
        offset,
        ...(difficulty ? { difficulty } : {}),
      });
      setTotal(res.data.total);
      setItems((prev) => (offset === 0 ? res.data.questions : [...prev, ...res.data.questions]));
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app, skill.id, difficulty]);

  const selectable = items.filter((q) => !q.already_added);
  const has = (q) => refs.includes(refKey(q.kind, q.id));
  const toggle = (q) =>
    onChange(has(q) ? refs.filter((r) => r !== refKey(q.kind, q.id)) : [...refs, refKey(q.kind, q.id)]);

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
        <select
          className="gp-select"
          value={difficulty}
          onChange={(e) => setDifficulty(e.target.value)}
          aria-label="تصفية حسب الصعوبة"
        >
          <option value="">جميع المستويات</option>
          {Object.entries(DIFFICULTY_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="gp-btn gp-btn-sm"
          onClick={() => onChange([...new Set([...refs, ...selectable.map((q) => refKey(q.kind, q.id))])])}
        >
          تحديد المعروض
        </button>
        <button type="button" className="gp-btn gp-btn-sm" onClick={() => onChange([])}>
          مسح التحديد
        </button>
        <span className="gp-badge ok">المحدد: {refs.length}</span>
        <small className="gp-muted">الإجمالي في المهارة: {total}</small>
      </div>

      {error && <div className="gp-error">{error}</div>}

      <div style={{ maxHeight: 360, overflowY: "auto", border: "1px solid #eee", borderRadius: 8 }}>
        {items.map((q) => (
          <QuestionRow key={refKey(q.kind, q.id)} q={q} checked={has(q)} onToggle={() => toggle(q)} />
        ))}
        {loading && <Spinner />}
        {!loading && items.length === 0 && <p className="gp-muted" style={{ padding: 12 }}>لا توجد أسئلة.</p>}
      </div>

      {!loading && items.length < total && (
        <button type="button" className="gp-btn gp-btn-sm" style={{ marginTop: 8 }} onClick={() => fetchPage(items.length)}>
          تحميل المزيد
        </button>
      )}
    </div>
  );
}

/* ───────────── شريط ملخص عدد الأسئلة المختارة ───────────── */

function SelectionSummary({ skills }) {
  const total = skills.reduce((sum, s) => sum + selectedCount(s), 0);
  const approx = skills.some((s) => s.mode !== "pick");

  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: 1,
        background: "#fff",
        border: "1px solid #bfdbfe",
        borderRadius: 8,
        padding: "10px 14px",
        marginBottom: 12,
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <b style={{ fontSize: 14 }}>
          إجمالي الأسئلة المختارة: {total}
          {approx ? " (تقريبي)" : ""}
        </b>
        <span className="gp-badge">عدد المهارات: {skills.length}</span>
      </div>
      {approx && (
        <small className="gp-muted" style={{ display: "block", marginTop: 4 }}>
          قد يقل العدد الفعلي إذا كانت بعض الأسئلة مُضافة مسبقًا إلى الاختبار أو عند تطبيق تصفية الصعوبة.
        </small>
      )}
    </div>
  );
}

/* ───────────── المودال الرئيسي ───────────── */

export default function QuizImportModal({ quiz, onClose, onImported, notify }) {
  const [apps, setApps] = useState(null);
  const [app, setApp] = useState(null);
  const [path, setPath] = useState([]); // [{level, id, title}]
  const [browse, setBrowse] = useState(null); // {level, items}
  const [selected, setSelected] = useState({}); // {skillId: config}
  const [step, setStep] = useState("browse"); // browse | config
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const params = useMemo(() => paramsFrom(path), [path]);

  useEffect(() => {
    quizService
      .sourceApps()
      .then((res) => setApps(res.data))
      .catch((e) => {
        setError(getErrorMessage(e));
        setApps([]);
      });
  }, []);

  useEffect(() => {
    if (!app) return undefined;
    let off = false;
    setBrowse(null);
    quizService
      .browseSource(app, params)
      .then((res) => !off && setBrowse(res.data))
      .catch((e) => {
        if (off) return;
        setError(getErrorMessage(e));
        setBrowse({ level: "", items: [] });
      });
    return () => {
      off = true;
    };
  }, [app, params]);

  const chooseApp = (key) => {
    setApp(key);
    setPath([]);
    setSelected({});
    setError("");
  };

  const enter = (item) => setPath((p) => [...p, { level: browse.level, id: item.id, title: item.title }]);
  const goTo = (index) => setPath((p) => p.slice(0, index));

  const isSkillLevel = browse?.level === "skill";
  const toggleSkill = (item) =>
    setSelected((prev) => {
      if (prev[item.id]) {
        const { [item.id]: _removed, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [item.id]: { ...item, mode: "pick", refs: [], random_count: 10, difficulty: "" },
      };
    });
  const updateSkill = (id, patch) => setSelected((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const skills = Object.values(selected);
  const totalSelected = skills.reduce((sum, s) => sum + selectedCount(s), 0);

  const submit = async () => {
    const selections = [];
    for (const s of skills) {
      if (s.mode === "pick") {
        if (!s.refs.length) return setError(`اختر أسئلة من مهارة "${s.title}" أو غيّر طريقة الاختيار`);
        selections.push({
          skill_id: s.id,
          refs: s.refs.map((r) => {
            const [kind, id] = r.split(":");
            return { kind, id: Number(id) };
          }),
        });
      } else if (s.mode === "all") {
        selections.push({ skill_id: s.id, select_all: true, ...(s.difficulty ? { difficulty: s.difficulty } : {}) });
      } else {
        const n = Number(s.random_count);
        if (!n || n < 1) return setError(`أدخل عددًا صحيحًا للمهارة "${s.title}"`);
        selections.push({
          skill_id: s.id,
          random_count: n,
          ...(s.difficulty ? { difficulty: s.difficulty } : {}),
        });
      }
    }

    setSaving(true);
    setError("");
    try {
      const res = await quizService.importQuestions(quiz.id, { app, selections });
      const d = res.data;
      notify(
        d.skipped_duplicates
          ? `تمت إضافة ${d.added} سؤالًا (تم تخطي ${d.skipped_duplicates} سؤالًا مكررًا)`
          : `تمت إضافة ${d.added} سؤالًا`
      );
      onImported();
      onClose();
    } catch (e) {
      setError(getErrorMessage(e));
      setSaving(false);
    }
  };

  const footer =
    step === "browse" ? (
      <>
        <button
          className="gp-btn gp-btn-primary"
          disabled={!skills.length}
          onClick={() => {
            setError("");
            setStep("config");
          }}
        >
          التالي{skills.length ? ` (${skills.length} مهارة)` : ""}
        </button>
        <button className="gp-btn" onClick={onClose}>
          إلغاء
        </button>
      </>
    ) : (
      <>
        <button className="gp-btn gp-btn-primary" onClick={submit} disabled={saving}>
          {saving ? "جارٍ الاستيراد..." : `استيراد الأسئلة (${totalSelected})`}
        </button>
        <button className="gp-btn" onClick={() => setStep("browse")} disabled={saving}>
          رجوع
        </button>
      </>
    );

  return (
    <Modal title={`استيراد أسئلة إلى: ${quiz.title}`} onClose={onClose} width={820} footer={app ? footer : null}>
      {error && <div className="gp-error">{error}</div>}

      {/* 1) اختيار التطبيق */}
      {!app &&
        (apps === null ? (
          <Spinner />
        ) : (
          <>
            <p className="gp-muted" style={{ marginBottom: 10 }}>
              اختر التطبيق الذي تريد استيراد الأسئلة منه:
            </p>
            <div className="gp-actions">
              {apps.map((a) => (
                <button key={a.key} className="gp-btn" onClick={() => chooseApp(a.key)}>
                  {a.label}
                </button>
              ))}
            </div>
          </>
        ))}

      {/* 2) التصفح واختيار المهارات */}
      {app && step === "browse" && (
        <>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
            <button className="gp-btn gp-btn-sm" onClick={() => setApp(null)}>
              {apps?.find((a) => a.key === app)?.label} ✕
            </button>
            {path.map((p, i) => (
              <button key={`${p.level}-${p.id}`} className="gp-btn gp-btn-sm" onClick={() => goTo(i)}>
                {p.title}
              </button>
            ))}
          </div>

          {browse === null ? (
            <Spinner />
          ) : browse.items.length === 0 ? (
            <p className="gp-muted">لا توجد عناصر في هذا المستوى.</p>
          ) : (
            <>
              <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 8 }}>
                {isSkillLevel ? "اختر مهارة أو أكثر" : `اختر ${LEVEL_LABELS[browse.level] || ""}`}
              </div>
              <div style={{ maxHeight: 340, overflowY: "auto" }}>
                {browse.items.map((item) =>
                  isSkillLevel ? (
                    <label
                      key={item.id}
                      style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 4px", borderBottom: "1px solid #eee", cursor: "pointer" }}
                    >
                      <input type="checkbox" checked={!!selected[item.id]} onChange={() => toggleSkill(item)} />
                      <span style={{ flex: 1 }}>{item.title}</span>
                      <span className="gp-badge">{SKILL_LABELS[item.skill_type] || item.skill_type}</span>
                      <span className="gp-badge">{item.questions_count} سؤال</span>
                    </label>
                  ) : (
                    <button
                      key={item.id}
                      className="gp-btn"
                      style={{ display: "block", width: "100%", textAlign: "start", marginBottom: 6 }}
                      onClick={() => enter(item)}
                    >
                      {item.title}
                    </button>
                  )
                )}
              </div>
            </>
          )}

          {skills.length > 0 && (
            <div className="gp-actions" style={{ marginTop: 12 }}>
              <small className="gp-muted">المهارات المحددة ({skills.length}):</small>
              {skills.map((s) => (
                <span key={s.id} className="gp-badge ok">
                  {s.title}
                </span>
              ))}
            </div>
          )}
        </>
      )}

      {/* 3) تحديد طريقة الاستيراد لكل مهارة */}
      {app && step === "config" && (
        <>
          <SelectionSummary skills={skills} />

          {skills.map((s) => (
            <div key={s.id} className="gp-panel" style={{ padding: 14, marginBottom: 12 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <b>{s.title}</b>
                <small className="gp-muted">{s.questions_count} سؤال متاح</small>
                <span className="gp-badge ok" style={{ marginInlineStart: "auto" }}>
                  المختار: {selectedCount(s)}
                </span>
              </div>

              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 10 }}>
                {MODES.map((m) => (
                  <label key={m.key} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13.5 }}>
                    <input
                      type="radio"
                      name={`mode-${s.id}`}
                      checked={s.mode === m.key}
                      onChange={() => updateSkill(s.id, { mode: m.key })}
                    />
                    {m.label}
                  </label>
                ))}
              </div>

              {s.mode === "pick" && (
                <SkillQuestionPicker
                  app={app}
                  skill={s}
                  quizId={quiz.id}
                  refs={s.refs}
                  onChange={(refs) => updateSkill(s.id, { refs })}
                />
              )}

              {s.mode !== "pick" && (
                <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                  {s.mode === "random" && (
                    <input
                      type="number"
                      min={1}
                      className="gp-input"
                      style={{ width: 110 }}
                      value={s.random_count}
                      onChange={(e) => updateSkill(s.id, { random_count: e.target.value })}
                      aria-label="العدد"
                    />
                  )}
                  <select
                    className="gp-select"
                    value={s.difficulty}
                    onChange={(e) => updateSkill(s.id, { difficulty: e.target.value })}
                    aria-label="الصعوبة"
                  >
                    <option value="">جميع المستويات</option>
                    {Object.entries(DIFFICULTY_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </Modal>
  );
}